import { POINT_SCALE } from "@/lib/grooming/plain-words";
import { cn } from "@/lib/utils";

export interface PointScaleProps {
  /** Lowest likely size. */
  low: number;
  /** Highest likely size. */
  high: number;
  /** The suggested size; highlighted in Toro red. */
  pick: number;
  /**
   * `lg` on the item summary and review pane, `sm` in agenda rows, `xl` on
   * Present mode's slides (design `.scale.xl`).
   */
  size?: "sm" | "lg" | "xl";
  className?: string;
}

const CELL_SIZE: Record<NonNullable<PointScaleProps["size"]>, string> = {
  sm: "h-6 w-7 rounded-lg text-xs",
  lg: "h-[38px] w-[46px] rounded-lg text-[15px]",
  xl: "h-[72px] w-[84px] rounded-xl text-[26px]",
};

const CELL_BASE = "flex items-center justify-center border font-semibold";
const CELL_OUT = "border-secondary bg-[#111113] text-zinc-600";

/**
 * The Story Point scale 1, 2, 3, 5, 8, 13 with the likely range tinted and
 * the suggested size picked (Claude Design "Final PM" frame 5b). A visual
 * aid only: the label carries the same facts for screen readers.
 */
export function PointScale({ low, high, pick, size = "lg", className }: PointScaleProps) {
  const label =
    low === high
      ? `Suggested size ${String(pick)} points`
      : `Suggested size ${String(pick)} points, likely between ${String(low)} and ${String(high)}`;

  return (
    <div role="img" aria-label={label} className={cn("flex gap-1", className)} data-testid="point-scale">
      {POINT_SCALE.map((value) => {
        const picked = value === pick;
        const inRange = !picked && value >= low && value <= high;
        return (
          <span
            key={value}
            aria-hidden="true"
            data-point={value}
            data-point-state={picked ? "pick" : inRange ? "range" : "out"}
            className={cn(
              CELL_BASE,
              CELL_SIZE[size],
              picked
                ? "border-primary bg-primary text-primary-foreground"
                : inRange
                  ? "border-primary/25 bg-primary/[0.12] text-[#FF8A9B]"
                  : CELL_OUT
            )}
          >
            {value}
          </span>
        );
      })}
    </div>
  );
}

/**
 * The point scale with Claude's suggestion withheld (Present mode, design
 * `.scale.hid`): the same blurred row of cells, but every cell holds a
 * placeholder and none is picked or tinted. It takes no size at all, so the
 * suggestion is absent from the page, not merely blurred: someone watching
 * a shared screen cannot read it through a failed blur or the dev tools.
 */
export function HiddenPointScale({ size = "xl", className }: Pick<PointScaleProps, "size" | "className">) {
  return (
    <div
      role="img"
      aria-label="Claude's suggested size is hidden"
      className={cn("flex gap-1", className)}
      data-testid="point-scale-hidden"
    >
      {POINT_SCALE.map((_, index) => (
        <span
          // Interchangeable placeholders: the position is the identity, never the value.
          key={`hidden-${String(index)}`}
          aria-hidden="true"
          data-point-state="hidden"
          className={cn(CELL_BASE, CELL_SIZE[size], CELL_OUT, "opacity-35 blur-[6px]")}
        >
          ·
        </span>
      ))}
    </div>
  );
}
